import React, { FC, useState } from "react";
import { Button, message, Modal } from "antd";
import validator from "@rjsf/validator-ajv6";
import { withTheme } from "@rjsf/core";
import { RJSFSchema, ErrorTransformer, CustomValidator, FormValidation } from "@rjsf/utils";
import { Theme } from "@rjsf/antd";
import { useTranslation } from "react-i18next";
import $authHost from "../../http/auth";
import { Loading } from "../../components/loading";
import tenantConfig from "../../constants/tenantConfig.json";
import { deepCopy } from "../../utils/deepCopy";
import styles from "./tenants.module.css";

// @ts-ignore
const Form = withTheme(Theme);

interface Props {
  open: boolean;
  onClose: Function;
  onSubmit: Function;
}

export const CreateTenantModal: FC<Props> = ({ open, onClose, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [liveValidate, setLiveValidate] = useState(false);
  const [formData, setFormData] = useState({});
  const { t } = useTranslation("tenants");
  const tenantSchema = tenantConfig as RJSFSchema;

  const customValidate: CustomValidator = (formData, errors) => {
    if (formData.password !== formData.confirmPassword) {
      errors.password?.addError(t("passwordsDontMatch"));
      errors.confirmPassword?.addError(t("passwordsDontMatch"));
    }
    return Object.fromEntries(
      // @ts-ignore
      Object.entries(errors).filter(([_, value]) => value?.__errors?.length)
    ) as FormValidation;
  };

  const transformErrors: ErrorTransformer = (errors) => {
    return errors.map((error) => {
      if (error.name === "required") {
        error.message = t("requiredField");
      }
      if (error.name === "pattern" && error.property?.toLowerCase().includes("password")) {
        error.message = t("passwordPatternError");
      }
      if (error.name === "pattern" && error.property?.toLowerCase().includes("email")) {
        error.message = t("emailPatternError");
      }
      if (error.name === "pattern" && error.property?.toLowerCase().includes("users")) {
        error.message = t("usersEmailPatternError");
      }
      if (error.name === "pattern" && error.property?.toLowerCase().includes("imos")) {
        error.message = t("imoPatternError");
      }
      return error;
    });
  };

  const handleFormDataChange = ({ formData }: any) => {
    setFormData((currentFormData) => ({ ...currentFormData, ...formData }));
  };

  const turnOnLiveValidate = () => {
    setLiveValidate(true);
  };

  const handleSubmit = () => {
    setSubmitting(true);

    const postingData = deepCopy(formData) as any;
    if (postingData.imos) {
      postingData.imos = postingData.imos.map((imo: string) => Number(imo));
    }

    if (postingData.companyName) {
      postingData.companyCode = postingData.companyName;
    }

    $authHost
      .post("/Tenants", postingData)
      .then((res) => {
        onSubmit(res.data);
        message.success(t("userAdded"));
        closeModal();
      })
      .catch(() => {
        setSubmitting(false);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const closeModal = () => {
    setFormData({});
    setLiveValidate(false);
    onClose();
  };

  return (
    <Modal title={t("createTenant")} open={open} onCancel={closeModal} footer={null} destroyOnClose={true}>
      <div className={styles.modalForm}>
        {submitting && <Loading />}
        <Form
          schema={tenantSchema}
          validator={validator}
          liveValidate={liveValidate}
          customValidate={customValidate}
          transformErrors={transformErrors}
          showErrorList={false}
          onChange={handleFormDataChange}
          onError={turnOnLiveValidate}
          formData={formData}
          onSubmit={handleSubmit}
        >
          <div className={styles.modalButtonContainer}>
            <Button type="primary" htmlType={"submit"} id={"submit-btn"}>
              {t("submit")}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
